import { SelectChangeEvent, Stack, Typography } from '@mui/material';
import { some } from 'lodash';
import { useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { LinkButton } from '@/components/form/baseInputs/Link';
import { SelectInput } from '@/components/form/baseInputs/SelectInput';
import { getOptionsWithGroupLabels } from '@/components/form/baseInputs/SelectInput/SelectInput.utils';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { mapDocumentToOption } from '@/modules/entities/components/EntityNotesPerDeath/GenerateAINotesModal.util';
import {
  GetEntityDetails_Entity_DocumentFragment,
  useGetHouseholdDocumentsQuery,
} from '@/modules/entities/components/EntityNotesPerDeath/graphql/GetHouseholdDocuments.generated';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { ROUTE_KEYS } from '@/navigation/constants';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { EntityKind } from '@/types/schema';
import { DocumentType } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { ENTITY_TYPES, TRUST_ENTITY_TYPES } from '../../entities.constants';
import { EntityType } from '../../types/EntityType';

/**
 * Returns the document types available for summary-based presentation notes.
 */
export function getDocumentTypesFromEntityType(
  entityType: EntityType | ''
): DocumentType[] {
  if (TRUST_ENTITY_TYPES.includes(entityType as EntityType))
    return [
      DocumentType.SignedTrustDocument,
      DocumentType.TrustAmendment,
      DocumentType.TrustRelatedDocument,
      DocumentType.CertificateOfTrust,
    ];
  else if (entityType === ENTITY_TYPES.LLC)
    return [DocumentType.LlcAgreement, DocumentType.OperatingAgreement];
  else if (entityType === ENTITY_TYPES.LP || entityType === ENTITY_TYPES.GP)
    return [DocumentType.PartnershipAgreement];
  else if (
    entityType === ENTITY_TYPES.S_CORP ||
    entityType === ENTITY_TYPES.C_CORP
  )
    return [
      DocumentType.OperatingAgreement,
      DocumentType.ShareholdersAgreement,
    ];
  return [DocumentType.Other];
}

export interface GenerateAIEntityNotesModalProps {
  entityId: string;
  entityKind: EntityKind;
  householdId: string;
  isOpen: boolean;
  handleClose: () => void;
  handleGenerate: (documentSummaryId: string) => void;
  isGenerating: boolean;
}

export function GenerateAINotesModal({
  entityId,
  entityKind,
  householdId,
  isOpen,
  handleClose,
  handleGenerate,
  isGenerating,
}: GenerateAIEntityNotesModalProps) {
  const { navigate } = useNavigateToRoute();

  const [documents, setDocuments] = useState<
    GetEntityDetails_Entity_DocumentFragment[]
  >([]);

  const [documentSummaryId, setDocumentSummaryId] = useState<string>('');

  const { loading } = useGetHouseholdDocumentsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
    variables: {
      documentWhere: {
        hasHouseholdWith: [{ id: householdId }],
        typeIn: getDocumentTypesFromEntityType(
          getEntityTypeFromEntityKind(entityKind)
        ),
      },
    },
    onCompleted: (data) => {
      if (!data) return;
      const documents = getNodes(data.documents);
      setDocuments(documents);

      const defaultDocument = documents.find((d) => d.entity?.id === entityId);
      // default to the first summary for the default document
      setDocumentSummaryId(defaultDocument?.summaries?.[0]?.id ?? '');
    },
    onError: (error) => {
      diagnostics.error(error.message);
    },
  });

  const options = useMemo(() => {
    const associatedDocumentsFilter = (
      d: GetEntityDetails_Entity_DocumentFragment
    ) => {
      return some(getNodes(d.entityDocumentRelationships), (r) => {
        return r.entityID === entityId;
      });
    };
    const otherDocumentsFilter = (
      d: GetEntityDetails_Entity_DocumentFragment
    ) => {
      return !some(getNodes(d.entityDocumentRelationships), (r) => {
        return r.entityID === entityId;
      });
    };

    return getOptionsWithGroupLabels([
      {
        label: 'Associated Documents',
        options: documents
          .filter(associatedDocumentsFilter)
          .flatMap(mapDocumentToOption),
      },
      {
        label: 'Other Documents',
        options: documents
          .filter(otherDocumentsFilter)
          .flatMap(mapDocumentToOption),
      },
    ]);
  }, [entityId, documents]);

  return (
    <LuminaryThemeProvider>
      <DialogModal
        isOpen={isOpen}
        onClose={handleClose}
        heading="Generate notes"
        dialogContentSx={{
          maxWidth: '600px',
          pb: 4,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="body-s">
            Client presentation notes will be generated based on the summary
            from the selected document.
          </Typography>
          <SelectInput
            // @ts-ignore -- CompoundSelectInputOption<string>
            options={options}
            label="Document summary"
            value={documentSummaryId}
            onChange={(e: SelectChangeEvent) => {
              setDocumentSummaryId(e.target.value);
            }}
            loading={loading}
            disabled={documents.length === 0}
            emptyValueDisplay="Select document"
            showEmptyValue={documents.length === 0}
            hideLabel
            testId="select-household-documents"
          />
          <Button
            size="sm"
            variant="primary"
            onClick={() => handleGenerate(documentSummaryId)}
            disabled={isGenerating || !documentSummaryId}
          >
            Generate
          </Button>
          {!documents.length && (
            <Stack alignItems="center">
              <LinkButton
                onClick={() => {
                  navigate(ROUTE_KEYS.HOUSEHOLD_ENTITY_DOCUMENTS, {
                    householdId,
                    entityId,
                  });
                }}
                display="Upload document"
              />
            </Stack>
          )}
        </Stack>
      </DialogModal>
    </LuminaryThemeProvider>
  );
}
